import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Divider, Chip,
  Skeleton, Alert, IconButton, Tooltip,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { Check, Info, ChartNoAxesCombined, Minus, Plus } from 'lucide-react';
import CustomDialogSmall from '../../../../components/Dialog/DiaCompSmall';
import CustomDialog from '../../../../components/Dialog/DiaCompMedium';
import MarkerTrendChart from '../../Bloodmarker/ReuseComps/MarkerTrendChart';
import TextInfoComp from '../../Bloodmarker/ReuseComps/TextInfoComp';
import { useTheme, alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { API_URL } from '../../../../constants';
import axios from 'axios';
import { useAuth } from '../../../../provider';
import { useTranslation } from '../../../../language';
import { pressable, pressSx, PRESS_ACTIVE } from "../../../../components/Pressable/pressable";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CAROUSEL_VISIBLE = 2;

// Forces a "Name (Detail)" marker label to break before the parenthetical,
// instead of relying on the browser to wrap it wherever it happens to fit.
function renderMarkerName(name) {
  const idx = name.indexOf(' (');
  if (idx === -1) return name;
  return (
    <>
      {name.slice(0, idx)}
      <br />
      {name.slice(idx + 1)}
    </>
  );
}

const MECHANISM_MARKER_SHORTS = {
  'Hepatic function and fatty liver risk':   ['Alt', 'Ast', 'AstAltRatio', 'Ggt', 'GGTHDLRatio', 'Fib4', 'Bill', 'Alp', 'Alba', 'RATIO63', 'AERATIO'],
  'Renal function and microvascular health': ['CreaGfr', 'Crea', 'Bun', 'BunCreaRatio'],
  'Tissue damage and metabolic by-products': ['Uric', 'Cre', 'Ldh', 'Lip', 'PanAmy', 'Test', 'Dhea', 'Ctx', 'Ost', 'Pth', 'Tsh', 'T3', 'T4', 'INDEX3'],
};

const MECHANISM_TITLE_KEYS = {
  'Hepatic function and fatty liver risk':   'mechanism_hepatic_function',
  'Renal function and microvascular health': 'mechanism_renal_function',
  'Tissue damage and metabolic by-products': 'mechanism_tissue_damage',
};

const MECHANISM_INFO_KEYS = {
  'Hepatic function and fatty liver risk':   'mechanism_hepatic_info',
  'Renal function and microvascular health': 'mechanism_renal_info',
  'Tissue damage and metabolic by-products': 'mechanism_tissue_info',
};

function getRiskMeta(score) {
  if (score <= 3) return { riskLevel: 'low' };
  if (score <= 6) return { riskLevel: 'med' };
  return             { riskLevel: 'high' };
}

function parseDateParts(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return { month: '—', day: '—', year: '—' };
  return {
    month: d.toLocaleString('en', { month: 'short' }).toUpperCase(),
    day:   String(d.getDate()).padStart(2, '0'),
    year:  String(d.getFullYear()),
  };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DateCarousel({ dates, selectedDate, onSelect }) {
  const theme = useTheme();
  const reversed = [...dates].reverse();
  const [startIndex, setStartIndex] = useState(Math.max(0, reversed.length - CAROUSEL_VISIBLE));

  useEffect(() => {
    setStartIndex(Math.max(0, reversed.length - CAROUSEL_VISIBLE));
  }, [reversed.length]);

  const canPrev = startIndex > 0;
  const canNext = startIndex + CAROUSEL_VISIBLE < reversed.length;
  const visible = reversed.slice(startIndex, startIndex + CAROUSEL_VISIBLE);

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center',
      borderRadius: 4, px: 1, py: 1.5,
      bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
      backdropFilter: 'blur(28px) saturate(180%)',
      WebkitBackdropFilter: 'blur(28px) saturate(180%)',
      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
      boxShadow: theme.palette.mode === 'dark'
        ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
        : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
    }}>
      <IconButton size="small" onClick={() => setStartIndex(i => i - 1)} disabled={!canPrev}
        sx={{ color: theme.palette.mode === 'light' ? 'greydark.C900' : 'grey.C50' }}>
        <ChevronLeftIcon />
      </IconButton>
      <Box sx={{ display: 'flex', gap: 1.5, mx: 0.5 }}>
        {visible.map((date) => {
          const { month, day, year } = parseDateParts(date);
          const selected = date === selectedDate;
          return (
            <Box key={date} {...pressable(() => onSelect(date))} aria-pressed={selected} sx={{
              ...pressSx,
              [PRESS_ACTIVE]: { transform: selected ? 'scale(1.07)' : 'scale(0.97)' },
              '@media (prefers-reduced-motion: reduce)': { [PRESS_ACTIVE]: { transform: selected ? 'scale(1.1)' : 'none' } },
              cursor: 'pointer',
              width: 82, py: selected ? 1.75 : 1.25, px: 0.5, mx: selected ? 1.5 : 0,
              borderRadius: 3, border: '2px solid',
              borderColor: selected ? 'primary.main' : (theme.palette.mode === 'light' ? 'grey.C900' : 'greydark.C400'),
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              bgcolor: selected ? alpha(theme.palette.primary.main, 0.09) : 'transparent',
              transform: selected ? 'scale(1.1)' : 'scale(1)',
              boxShadow: selected ? `0 4px 18px ${alpha(theme.palette.primary.main, 0.25)}` : 'none',
              zIndex: selected ? 1 : 0,
              transition: 'all 0.2s ease', userSelect: 'none',
            }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: selected ? 'primary.main' : 'text.secondary', letterSpacing: 0.5, fontSize: selected ? '0.8rem' : '0.72rem' }}>
                {month}
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.15, my: 0.25, color: selected ? 'primary.main' : 'text.primary', fontSize: selected ? '1.75rem' : '1.35rem' }}>
                {day}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: selected ? '0.8rem' : '0.72rem' }}>
                {year}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <IconButton size="small" onClick={() => setStartIndex(i => i + 1)} disabled={!canNext}
        sx={{ color: theme.palette.mode === 'light' ? 'greydark.C900' : 'grey.C50' }}>
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
}

function RiskDots({ activeCount = 1, riskLevel = 'low' }) {
  const theme = useTheme();
  const p = theme.palette;
  const trafficColors = { low: p.traffic.green, med: p.traffic.orange, high: p.traffic.red };
  const activeColor = trafficColors[riskLevel] ?? trafficColors.low;
  const inactiveColor = theme.palette.mode === 'dark' ? p.greydark.C600 : p.grey.C300;
  return (
    <Box sx={{ display: 'flex', gap: '6px', mb: 2.5 }}>
      {Array.from({ length: 10 }).map((_, i) => (
        <Box key={i} sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: i < activeCount ? activeColor : inactiveColor, flexShrink: 0 }} />
      ))}
    </Box>
  );
}

function MechanismSection({ title, infoKey, activeCount, riskLevel, description, markerShorts, markersMap, visitResults }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const dark = theme.palette.mode === 'dark';
  const p = theme.palette;
  const titleColor = dark ? p.grey.C50  : p.greydark.C900;
  const bodyColor  = dark ? p.grey.C400 : p.greydark.C500;
  const red = p.traffic.red;

  const [mechInfoOpen, setMechInfoOpen] = useState(false);
  const [chartOpen, setChartOpen]       = useState(false);
  const [chartMarker, setChartMarker]   = useState(null);
  const [openInfo, setOpenInfo]         = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);

  return (
    <Box>
      <Divider sx={{ mb: 4 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '4fr 8fr' }, gap: { xs: 3, lg: 6 }, mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: titleColor, fontSize: '1.05rem' }}>
              {title}
            </Typography>
            {infoKey && (
              <IconButton size="small" onClick={() => setMechInfoOpen(true)} sx={{ '&:hover': { transform: 'scale(1.2)' } }}>
                <Info size={18} color={titleColor} />
              </IconButton>
            )}
          </Box>
          <RiskDots activeCount={activeCount} riskLevel={riskLevel} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Typography variant="body1" sx={{ color: bodyColor, lineHeight: 1.75, fontWeight: 400 }}>
            {description}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '4fr 8fr' }, gap: { xs: 3, lg: 6 }, mb: 5 }}>
        <Box />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: titleColor }}>{t('intervention_targets')}</Typography>
          {(() => {
            const abnormalShorts = markerShorts.filter(short => {
              const r = markersMap?.[short];
              return r && r.status?.toUpperCase() !== 'NORMAL';
            });

            if (abnormalShorts.length === 0) {
              return (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 2, py: 1.75, borderRadius: 2, border: '1px dashed',
                  borderColor: p.traffic.green,
                  bgcolor: dark ? `${p.traffic.green}11` : `${p.traffic.green}0d`,
                }}>
                  <Check size={16} strokeWidth={2.5} color={p.traffic.green} style={{ flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: p.traffic.green, fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.5 }}>
                    {t('intervention_targets_all_normal')}
                  </Typography>
                </Box>
              );
            }

            return (
              <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderLeft: `10px solid ${p.tertiary.C300}` }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '2fr 1fr 1fr', sm: '2fr 1fr 1fr 1fr 1fr' }, px: 1.5, py: 0.5, bgcolor: dark ? 'greydark.C800' : 'grey.C200' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontSize: '0.6rem', minWidth: 0 }}>
                    {t('bt_chart_col_marker')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontSize: '0.6rem' }}>
                    {t('bt_chart_col_value')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontSize: '0.6rem' }}>
                    {t('bt_chart_col_unit')}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontSize: '0.6rem' }}>
                    {t('bt_chart_col_status')}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontSize: '0.6rem' }} />
                </Box>
                {abnormalShorts.map((short, idx) => {
                  const result = markersMap?.[short];
                  const statusKey = `bt_status_${(result?.status || '').toLowerCase().replace(/\s+/g, '_')}`;
                  const abnormalLabel = result ? (t(statusKey) !== statusKey ? t(statusKey) : result.status) : '';
                  return (
                    <Box key={short} sx={{
                      display: 'grid', gridTemplateColumns: { xs: '2fr 1fr 1fr', sm: '2fr 1fr 1fr 1fr 1fr' },
                      pl: 1.5, pr: 0, py: 0.4, alignItems: 'center',
                      bgcolor: idx % 2 === 0 ? (dark ? 'greydark.C900' : 'grey.C50') : (dark ? 'greydark.C800' : 'grey.C100'),
                    }}>
                      <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.75rem', color: result ? 'text.primary' : 'text.disabled', minWidth: 0 }}>
                        {renderMarkerName(t(`markername_${short}`))}
                      </Typography>
                      <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem', fontWeight: result?.value != null ? 600 : 400, color: result?.value != null ? 'text.primary' : 'text.disabled' }}>
                        {result?.value != null ? Number(result.value).toFixed(2) : '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '0.75rem' }}>
                        {result?.unit ?? '—'}
                      </Typography>
                      <Box>
                        {result
                          ? <Typography variant="caption" fontWeight={700} sx={{ color: red, fontSize: '0.65rem' }}>{abnormalLabel}</Typography>
                          : <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem', fontStyle: 'italic' }}>{t('marker_not_ordered')}</Typography>
                        }
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                        {result && (
                          <>
                            <Tooltip title={t('bt_chart_title')} placement="top">
                              <IconButton size="small" onClick={() => { setChartMarker(short); setChartOpen(true); }}
                                sx={{ p: 0.25, '&:hover': { transform: 'scale(1.2)' } }}>
                                <ChartNoAxesCombined size={15} color={theme.palette.text.secondary} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Info" placement="top">
                              <IconButton size="small" onClick={() => { setSelectedInfo({ short }); setOpenInfo(true); }}
                                sx={{ p: 0.25, '&:hover': { transform: 'scale(1.2)' } }}>
                                <Info size={15} color={theme.palette.text.secondary} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      </Box>

      {infoKey && (
        <CustomDialogSmall
          open={mechInfoOpen}
          onClose={() => setMechInfoOpen(false)}
          title={title}
          content={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="subtitle1" sx={{ lineHeight: 1.5, color: titleColor }}>{t(infoKey)}</Typography>
              <Divider />
              <Box>
                <Typography variant="body1" fontWeight={700} sx={{ mb: 1, letterSpacing: '0.005em', color: titleColor }}>{t('markers_in_focus')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                  {markerShorts.map(s => (
                    <Chip key={s} label={t(`markername_${s}`)} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 24 }} />
                  ))}
                </Box>
                <Typography variant="subtitle1" sx={{ lineHeight: 1.5, color: titleColor }}>{t('markers_analysis_scope')}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body1" fontWeight={700} sx={{ mb: 2, letterSpacing: '0.005em', color: titleColor }}>{t('score_what_title')}</Typography>
                <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: dark ? 'greydark.C900' : 'grey.C50' }}>
                  <RiskDots activeCount={activeCount} riskLevel={riskLevel} />
                  <Typography variant="subtitle1" sx={{ lineHeight: 1.5, color: titleColor }}>{t('score_scale_desc')}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { count: 2, level: 'low',  descKey: 'score_low_desc'  },
                  { count: 5, level: 'med',  descKey: 'score_med_desc'  },
                  { count: 8, level: 'high', descKey: 'score_high_desc' },
                ].map(({ count, level, descKey }) => {
                  const dotColor = { low: p.traffic.green, med: p.traffic.orange, high: p.traffic.red }[level];
                  const inactiveColor = dark ? p.greydark.C600 : p.grey.C300;
                  return (
                    <Box key={level} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        {Array.from({ length: 10 }).map((_, i) => (
                          <Box key={i} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: i < count ? dotColor : inactiveColor }} />
                        ))}
                      </Box>
                      <Typography variant="subtitle1" sx={{ lineHeight: 1.25, color: titleColor }}>{t(descKey)}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          }
        />
      )}

      <CustomDialogSmall
        open={openInfo}
        onClose={() => { setOpenInfo(false); setSelectedInfo(null); }}
        title="Information"
        content={selectedInfo && (
          <TextInfoComp
            description={`description_${selectedInfo.short}`}
            lowtext={`lowtext_${selectedInfo.short}`}
            lowrec={`lowrec_${selectedInfo.short}`}
            rangetext={`rangetext_${selectedInfo.short}`}
            hightext={`hightext_${selectedInfo.short}`}
            highrec={`highrec_${selectedInfo.short}`}
          />
        )}
      />

      <CustomDialog
        open={chartOpen}
        onClose={() => { setChartOpen(false); setChartMarker(null); }}
        title={chartMarker ? `${t(`markername_${chartMarker}`)} — ${t('bt_chart_title')}` : ''}
        content={chartMarker && (
          <MarkerTrendChart
            markerShort={chartMarker}
            visitOptions={visitResults ?? []}
            userEntries={[]}
            theme={theme}
            t={t}
          />
        )}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

function DateSkeleton() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 4 }}>
      {Array.from({ length: CAROUSEL_VISIBLE }).map((_, i) => (
        <Skeleton key={i} variant="rounded" width={82} height={88} sx={{ borderRadius: 3 }} />
      ))}
    </Box>
  );
}

function ContentSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Paper elevation={0} sx={(t) => ({ p: { xs: 3, md: 4 }, border: `1px solid ${t.palette.mode === 'dark' ? t.palette.greydark.C600 : t.palette.grey.C200}`, borderRadius: '12px', boxShadow: t.palette.mode === 'dark' ? `0 1px 8px 0 ${alpha(t.palette.common.black, 0.35)}` : `0 1px 6px 0 ${alpha(t.palette.common.black, 0.06)}` })}>
        <Skeleton variant="text" width={180} height={16} sx={{ mb: 1.5 }} />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="95%" />
        <Skeleton variant="text" width="80%" />
      </Paper>
      <Box>
        {[1, 2, 3].map((n) => (
          <Box key={n}>
            <Divider sx={{ mb: 4 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '4fr 8fr' }, gap: { xs: 3, lg: 6 }, mb: 2 }}>
              <Box>
                <Skeleton variant="text" width={160} height={24} sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', gap: '6px', mb: 0.5 }}>
                  {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} variant="circular" width={10} height={10} />)}
                </Box>
              </Box>
              <Box>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="70%" />
              </Box>
            </Box>
            <Box sx={{ mb: 5, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 90px 70px 90px', px: 2, py: 1, gap: 2 }}>
                {[120, 60, 40, 55].map((w, i) => <Skeleton key={i} variant="text" width={w} height={12} />)}
              </Box>
              {Array.from({ length: 4 }).map((_, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 90px 70px 90px', px: 2, py: 0.9, gap: 2 }}>
                  <Skeleton variant="text" /><Skeleton variant="text" width={55} /><Skeleton variant="text" width={35} /><Skeleton variant="text" width={50} />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
        <Divider />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function OrganHealth() {
  const { state }  = useAuth();
  const userId     = state.user.id;
  const theme      = useTheme();
  const { t }      = useTranslation();
  const dark       = theme.palette.mode === 'dark';
  const p          = theme.palette;
  const bodyColor     = dark ? p.grey.C400 : p.greydark.C500;
  const overlineColor = dark ? p.grey.C500 : p.grey.C700;

  const [dates, setDates]               = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [datesLoading, setDatesLoading] = useState(true);
  const [datesError, setDatesError]     = useState(null);

  const [visitResults, setVisitResults] = useState([]);


  const markersMap = useMemo(() => {
    if (!visitResults.length || !selectedDate) return {};
    const visit = visitResults.find((v) => v.visit_date === selectedDate);
    if (!visit) return {};
    return (visit.results ?? []).reduce((acc, r) => { acc[r.marker_short] = r; return acc; }, {});
  }, [visitResults, selectedDate]);

  const [summary, setSummary]           = useState('');
  const [mechanisms, setMechanisms]     = useState([]);
  const [infoOpen, setInfoOpen]         = useState(false);
  const [openInfoAccordions, setOpenInfoAccordions] = useState(new Set());
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError]     = useState(null);

  const fetchReport = useCallback(async (date) => {
    setReportLoading(true);
    setReportError(null);
    setSummary('');
    setMechanisms([]);
    try {
      const { data } = await axios.get(`${API_URL}/api/reports/${userId}/${date}/`);
      const pillar = data.pillar_summaries?.find((pillar) => pillar.subsystem === 'Organ Function');
      if (!pillar) { setReportError(t('organ_error_no_data')); return; }
      setSummary(pillar.summary);
      setMechanisms(
        (pillar.mechanism_scores ?? []).map((ms) => ({
          title:        t(MECHANISM_TITLE_KEYS[ms.mechanism] ?? ms.mechanism),
          infoKey:      MECHANISM_INFO_KEYS[ms.mechanism] ?? null,
          activeCount:  ms.risk_score,
          riskLevel:    getRiskMeta(ms.risk_score).riskLevel,
          markerShorts: MECHANISM_MARKER_SHORTS[ms.mechanism] ?? [],
          description:  ms.interpretation,
        }))
      );
    } catch (err) {
      const status = err.response?.status;
      setReportError(
        status === 404 ? t('organ_error_no_report')
        : status === 403 ? t('organ_error_forbidden')
        : t('organ_error_load')
      );
    } finally {
      setReportLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setDatesLoading(true);
      setDatesError(null);
      try {
        const { data } = await axios.get(`${API_URL}/api/all-lab-visits/results/`);
        if (cancelled) return;
        const labVisits = Array.isArray(data) ? data : (data.lab_visits ?? []);
        setVisitResults(labVisits);
        const sorted = [...new Set(labVisits.filter((v) => Number(v.state) === 6).map((v) => v.visit_date).filter(Boolean))]
          .sort((a, b) => new Date(b) - new Date(a));
        setDates(sorted);
        if (sorted.length > 0) { setSelectedDate(sorted[0]); fetchReport(sorted[0]); }
      } catch {
        if (!cancelled) setDatesError(t('organ_error_dates'));
      } finally {
        if (!cancelled) setDatesLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [userId, t, fetchReport]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: '900px', mx: 'auto', px: { xs: 2, sm: 4, md: 6 }, width: '100%' }}>

      {datesLoading ? (
        <DateSkeleton />
      ) : datesError ? (
        <Alert severity="error" sx={{ mb: 4, borderRadius: '10px' }}>{datesError}</Alert>
      ) : (
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
          <DateCarousel dates={dates} selectedDate={selectedDate} onSelect={(date) => { setSelectedDate(date); fetchReport(date); }} />
        </Box>
      )}

      {reportLoading ? (
        <ContentSkeleton />
      ) : reportError ? (
        <Alert severity="error" sx={{ borderRadius: '10px' }}>{reportError}</Alert>
      ) : summary ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

          <Paper elevation={0} sx={{
            p: { xs: 3, md: 4 }, overflow: 'hidden',
            borderRadius: 4,
            bgcolor: dark ? alpha(p.common.white, 0.04) : alpha(p.common.white, 0.5),
            backdropFilter: 'blur(28px) saturate(180%)',
            WebkitBackdropFilter: 'blur(28px) saturate(180%)',
            border: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
            boxShadow: dark
              ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
              : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="overline" sx={{ fontSize: '0.85rem', fontWeight: 800, letterSpacing: '0.05em', color: overlineColor }}>
                {t('organ_overall_summary')}
              </Typography>
              <IconButton size="small" onClick={() => setInfoOpen(true)} sx={{ '&:hover': { transform: 'scale(1.2)' } }}>
                <Info size={22} color={overlineColor} />
              </IconButton>
            </Box>
            <Typography variant="body1" sx={{ color: bodyColor, lineHeight: 1.75, fontWeight: 400 }}>
              {summary}
            </Typography>
          </Paper>

          <CustomDialog
            open={infoOpen}
            onClose={() => setInfoOpen(false)}
            title={t('organ_info_title')}
            content={
              <Box sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
                {[
                  {
                    titleKey: 'organ_what_title',
                    body: (
                      <>
                        <Typography variant="body1" sx={{ lineHeight: 1.75, mb: 1 }}>{t('organ_what_intro')}</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pl: 1, mb: 1.5 }}>
                          {['organ_what_li_1','organ_what_li_2','organ_what_li_3','organ_what_li_4','organ_what_li_5'].map((key) => (
                            <Box key={key} sx={{ display: 'flex', gap: 1 }}>
                              <Typography variant="body1" sx={{ lineHeight: 1.75, flexShrink: 0 }}>·</Typography>
                              <Typography variant="body1" sx={{ lineHeight: 1.75 }}>{t(key)}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.75, mb: 1 }}>{t('organ_what_poor')}</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pl: 1, mb: 1.5 }}>
                          {['organ_what_cond_1','organ_what_cond_2','organ_what_cond_3','organ_what_cond_4'].map((key) => (
                            <Box key={key} sx={{ display: 'flex', gap: 1 }}>
                              <Typography variant="body1" sx={{ lineHeight: 1.75, flexShrink: 0 }}>·</Typography>
                              <Typography variant="body1" sx={{ lineHeight: 1.75 }}>{t(key)}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.75 }}>{t('organ_what_closing')}</Typography>
                      </>
                    ),
                  },
                  {
                    titleKey: 'organ_info_what_title',
                    body: <Typography variant="body1" sx={{ lineHeight: 1.75 }}>{t('organ_info_what_body')}</Typography>,
                  },
                  {
                    titleKey: 'organ_info_how_title',
                    body: <Typography variant="body1" sx={{ lineHeight: 1.75 }}>{t('organ_info_how_body')}</Typography>,
                  },
                  {
                    titleKey: 'organ_info_benefit_title',
                    body: <Typography variant="body1" sx={{ lineHeight: 1.75 }}>{t('organ_info_benefit_body')}</Typography>,
                  },
                  {
                    titleKey: 'organ_takeaway_title',
                    body: (
                      <>
                        <Typography variant="body1" sx={{ lineHeight: 1.75, mb: 1 }}>{t('organ_takeaway_intro')}</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pl: 1 }}>
                          {['organ_takeaway_li_1','organ_takeaway_li_2','organ_takeaway_li_3','organ_takeaway_li_4','organ_takeaway_li_5','organ_takeaway_li_6'].map((key) => (
                            <Box key={key} sx={{ display: 'flex', gap: 1 }}>
                              <Typography variant="body1" sx={{ lineHeight: 1.75, flexShrink: 0 }}>·</Typography>
                              <Typography variant="body1" sx={{ lineHeight: 1.75 }}>{t(key)}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </>
                    ),
                  },
                ].map(({ titleKey, body }) => {
                  const isOpen = openInfoAccordions.has(titleKey);
                  return (
                    <Accordion
                      key={titleKey}
                      expanded={isOpen}
                      onChange={(e, expandedNow) => {
                        setOpenInfoAccordions((prev) => {
                          const next = new Set(prev);
                          if (expandedNow) next.add(titleKey); else next.delete(titleKey);
                          return next;
                        });
                      }}
                      disableGutters
                      elevation={0}
                      square
                      sx={{
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={isOpen
                          ? <Minus size={18} color={theme.palette.mode === 'light' ? theme.palette.greydark.C900 : theme.palette.grey.C50} />
                          : <Plus size={18} color={theme.palette.mode === 'light' ? theme.palette.greydark.C900 : theme.palette.grey.C50} />}
                        sx={{ px: 0 }}
                      >
                        <Typography variant="h6" fontWeight={isOpen ? 600 : 400} sx={{ letterSpacing: '0.015em' }}>{t(titleKey)}</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
                        {body}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            }
          />

          <Box>
            {mechanisms.map((m) => (
              <MechanismSection key={m.title} {...m} markersMap={markersMap} visitResults={visitResults} />
            ))}
            <Divider />
          </Box>

        </Box>
      ) : null}

    </Box>
  );
}
